import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Lock } from 'lucide-react';

// Kundenportal (eigenes System, eigene Domain)
const PORTAL_URL = 'https://someones-studio.fly.dev/anmelden';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);

    // Handle hash navigation after page load
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const handleAnchorClick = (e, path) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (path.includes('#')) {
      const [pagePath, hash] = path.split('#');
      const targetPage = pagePath || '/';

      if (location.pathname === targetPage || (targetPage === '/' && location.pathname === '/')) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(targetPage);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      navigate(path);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Leistungen', path: '/services' },
    { name: 'About us', path: '/#about' },
    { name: 'Kontakt', path: '/#contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' && !location.hash;
    if (path.includes('#')) return false;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-background/80 backdrop-blur-sm'
        }`}
      >
        <nav className="container-custom">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo: rote Bildmarke + Wortmarke */}
            <Link to="/" className="relative z-10 flex items-center gap-3 order-2 md:order-1" aria-label="Someones Direction, Startseite">
              <motion.img
                whileHover={{ rotate: -6 }}
                src="/logo-mark-red.png"
                alt=""
                className="h-9 md:h-10 w-auto"
              />
              <span className="hidden sm:block font-body text-sm md:text-base tracking-[0.35em] text-foreground font-semibold uppercase">
                Someones&nbsp;Direction
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8 lg:gap-10 md:order-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.path}
                  onClick={(e) => handleAnchorClick(e, link.path)}
                  className={`nav-link cursor-pointer ${isActive(link.path) ? 'active' : ''}`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Aktion rechts: Kundenportal */}
            <div className="hidden md:flex items-center md:order-3">
              <a
                href={PORTAL_URL}
                className="btn-primary !px-6 !py-2.5 text-xs inline-flex items-center gap-2"
                title="Kundenportal"
              >
                <Lock className="w-3.5 h-3.5" />
                Einloggen
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden relative z-10 p-2 -ml-2 order-1"
              aria-label="Menü öffnen"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background md:hidden"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <a
                    href={link.path}
                    onClick={(e) => handleAnchorClick(e, link.path)}
                    className="font-display text-3xl text-foreground hover:text-brand transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.45 }}
                className="flex flex-col items-center gap-6 pt-4"
              >
                <a
                  href={PORTAL_URL}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Einloggen
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
